import { useState, useEffect } from "react";
import { Layout, message, Typography, Grid } from "antd";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const { Content } = Layout;
const { useBreakpoint } = Grid;
const dataApiUrl = process.env.REACT_APP_OTPCountPerApp;

const FrequentApps = () => {
  const { Title } = Typography;
  const [messageApi, contextHolder] = message.useMessage();
  const [chartData, setChartData] = useState([]);
  const [allAppKeys, setAllAppKeys] = useState([]);
  const screens = useBreakpoint();

  // Color mapping — GoldPari is always red
  const colorMap = {
    GoldPari: "#FF0000",
    defaultColors: [
      "#8884d8",
      "#82ca9d",
      "#ffc658",
      "#ff7300",
      "#a4de6c",
      "#d0ed57",
      "#8dd1e1",
      "#83a6ed",
      "#ff9999",
      "#99ccff",
      "#c2f0c2",
      "#f0c2e0",
    ],
  };

  useEffect(() => {
    fetchChartData();
  }, []);

  const fetchChartData = async () => {
    try {
      const response = await fetch(dataApiUrl);
      if (!response.ok) throw new Error("Failed to fetch chart data");

      const data = await response.json();

      // 1️⃣ Get all unique app names
      let allApps = Array.from(
        new Set(Object.values(data).flatMap((apps) => Object.keys(apps)))
      );

      // 2️⃣ Move GoldPari to first position if it exists
      allApps = allApps.filter((app) => app !== "GoldPari");
      allApps.unshift("GoldPari");

      // 3️⃣ Build chart rows with all apps
      const formattedData = Object.keys(data).map((country) => {
        const row = { country };
        allApps.forEach((app) => {
          row[app] = data[country][app] || 0;
        });
        return row;
      });

      setChartData(formattedData);
      setAllAppKeys(allApps);
    } catch (error) {
      console.error("Error fetching chart data:", error);
      messageApi.error("Failed to load chart data");
    }
  };

  // Color assignment
  const getColor = (appName, index) => {
    if (appName === "GoldPari") return colorMap.GoldPari;
    return colorMap.defaultColors[index % colorMap.defaultColors.length];
  };

  return (
    <Layout>
      {contextHolder}
      <Content
        style={{
          padding: screens.md ? "0 18px" : "0 5px",
          minHeight: "100vh",
          background: "#fff",
        }}
      >
        <div className="mb-4">
          <Title level={3}>Frequently Used Apps (Manul Testers)</Title>
        </div>

        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={screens.md ? 450 : 320}>
            <BarChart
              data={chartData}
              margin={{
                top: 20,
                right: screens.md ? 30 : 10,
                left: 0,
                bottom: 50,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="country" interval={0} />
              <YAxis />
              <Tooltip formatter={(value, name) => [`${value} OTPs`, name]} />
              <Legend
                verticalAlign="bottom"
                wrapperStyle={{ paddingTop: 20 }}
              />

              {allAppKeys.map((appName, idx) => (
                <Bar
                  key={appName}
                  dataKey={appName}
                  fill={getColor(appName, idx)}
                  barSize={screens.md ? 40 : 25}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Content>
    </Layout>
  );
};

export default FrequentApps;
